import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

/**
 * Terms of Service Screen
 * - Public legal page for SafeVibe.dev
 * - Follows reference design: no card, no shadow, just centered content
 * - Uses Tailwind spacing, color, and font conventions
 * - No session or navigation logic
 * - Logs mount for debug/dev
 */
const TermsOfServiceScreen: React.FC = () => {
  useEffect(() => {
    // Minimal debug log for compliance
    console.log('Terms of Service screen loaded');
  }, []);

  return (
    <>
      <Navbar />
      <div className="max-w-2xl mx-auto py-12 px-4 text-gray-800" data-testid="terms-of-service-content">
        {/* Page Title */}
        <h1 className="text-3xl font-bold mb-4">Terms of Service – SafeVibe.dev</h1>
        {/* Meta Info */}
        <p className="mb-2 text-sm text-gray-500">Effective Date: 26.6.2025</p>
        <p className="mb-2 text-sm text-gray-500">Operator: SafeVibe.dev</p>
        <p className="mb-2 text-sm text-gray-500">Address: Beethovenstrasse 48, 8002 Zurich</p>
        <p className="mb-2 text-sm text-gray-500">Email: hello@safevibe.dev</p>

        {/* Section 1: Introduction */}
        <h2 className="text-xl font-semibold mt-8 mb-2">1. Introduction</h2>
        <p className="mb-4">Welcome to SafeVibe.dev, a developer tool designed to help early-stage builders scan their applications for potential security, privacy, and legal risks. By accessing or using our service, you agree to these Terms of Service (“Terms”). If you do not agree with these Terms, please do not use SafeVibe.dev.</p>

        {/* Section 2: Services Provided */}
        <h2 className="text-xl font-semibold mt-8 mb-2">2. Services Provided</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>Static code scans (e.g., exposed API keys, public storage)</li>
          <li>AI-powered fix suggestions (optional, not guaranteed to be correct)</li>
          <li>Legal & privacy issue detection (e.g., missing privacy policy, entity mismatch)</li>
        </ul>
        <p className="mb-4">The platform is intended for informational and educational purposes only and does not provide legal, security, or compliance guarantees.</p>

        {/* Section 3: User Eligibility */}
        <h2 className="text-xl font-semibold mt-8 mb-2">3. User Eligibility</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>Be at least 18 years old</li>
          <li>Have the legal right to submit the code or project for analysis</li>
          <li>Not use the service for any unlawful or malicious purposes</li>
        </ul>

        {/* Section 4: Data Handling */}
        <h2 className="text-xl font-semibold mt-8 mb-2">4. Data Handling</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>Deleted automatically within 7 days unless you upgrade</li>
          <li>Stored securely in EU-hosted Supabase storage</li>
          <li>Never sold or reused</li>
        </ul>
        <p className="mb-4">See our Privacy Policy for full details.</p>

        {/* Section 5: Payments */}
        <h2 className="text-xl font-semibold mt-8 mb-2">5. Payments</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>You agree to Stripe's terms of service and refund policy</li>
          <li>You receive access for the stated duration</li>
          <li>You may cancel at any time, but refunds are not guaranteed</li>
        </ul>

        {/* Section 6: Acceptable Use */}
        <h2 className="text-xl font-semibold mt-8 mb-2">6. Acceptable Use</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>Do not upload malicious, copyrighted, or unauthorized code</li>
          <li>Do not attempt to scan third-party projects without permission</li>
          <li>Do not reverse-engineer or tamper with SafeVibe.dev services</li>
        </ul>
        <p className="mb-4">Violation may result in account suspension or legal action.</p>

        {/* Section 7: Disclaimers */}
        <h2 className="text-xl font-semibold mt-8 mb-2">7. Disclaimers</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>SafeVibe.dev does not guarantee that all security or privacy issues will be detected.</li>
          <li>AI-generated suggestions are provided "as-is" and may contain errors.</li>
          <li>We are not liable for any damages, data loss, or legal issues resulting from your use of the platform.</li>
        </ul>

        {/* Section 8: Limitation of Liability */}
        <h2 className="text-xl font-semibold mt-8 mb-2">8. Limitation of Liability</h2>
        <p className="mb-4">To the maximum extent permitted by law, SafeVibe.dev and its operators disclaim all liability for any direct, indirect, incidental, or consequential damages arising from your use of this website or our services. Use is at your own risk.</p>

        {/* Section 9: Governing Law */}
        <h2 className="text-xl font-semibold mt-8 mb-2">9. Governing Law</h2>
        <p className="mb-4">These Terms are governed by Swiss law. Any disputes shall be subject to the exclusive jurisdiction of the courts in Zurich, Switzerland.</p>

        {/* Section 10: Modifications */}
        <h2 className="text-xl font-semibold mt-8 mb-2">10. Modifications</h2>
        <p className="mb-4">We may update these Terms as the product evolves. Changes will be posted on this page with a new effective date. Continued use constitutes acceptance of the updated Terms.</p>

        {/* Section 11: Contact */}
        <h2 className="text-xl font-semibold mt-8 mb-2">11. Contact</h2>
        <p className="mb-2">Email: hello@safevibe.dev</p>
        <p className="mb-2">Address: Beethovenstrasse 48, 8002 Zurich</p>

        {/* Back to Home link for navigation */}
        <div className="flex justify-end mt-8">
          <Link to="/" className="text-blue-600 font-semibold hover:underline">Back to Home</Link>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TermsOfServiceScreen; 